import { CdkDrag } from '@angular/cdk/drag-drop';
import {
  Component,
  ElementRef,
  Input,
  OnInit,
  Renderer2,
  ViewChild,
  ViewContainerRef,
  inject
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { AgGridService, AlertsService, ConfigService } from '@approot/services';
import { WindowIndexService } from '@approot/widgets/rfq/rfq-container/window-index.service';
import {
  AgGridEvent,
  ColDef,
  ColumnApi,
  ColumnState,
  GridApi,
  GridOptions,
  GridReadyEvent,
  SideBarDef
} from 'ag-grid-community';

import { clone } from 'lodash';
import { BackToBackChartComponent } from './back-to-back-trades-chart/back-to-back-trades-chart.component';
import { applyColumnState } from './back-to-back-trades.funcs';
import { BackToBackService } from './back-to-back-trades.service';
import { GetWhiteLabelRfqItem } from './back-to-back-trades.types';

const WIN_POSITION_MIN_X = 8;
const WIN_POSITION_MIN_Y = 36;
const WIN_POSITION_RIGHT_MIN = 100;

@Component({
  selector: 'app-back-to-back-trades',
  templateUrl: './back-to-back-trades.component.html',
  styleUrls: ['./back-to-back-trades.component.scss']
})
export class BackToBackComponent implements OnInit {
  @ViewChild(CdkDrag) public dragEl: CdkDrag;
  @ViewChild('modalContainer', { read: ViewContainerRef })
  public modalContainer: ViewContainerRef;
  @ViewChild('chartComponent')
  public chartComponent: BackToBackChartComponent;

  @Input() public rowData: GetWhiteLabelRfqItem[] = [];
  @Input() public timestamp: number;
  @Input() public index: number;
  @Input() public columnState: ColumnState[];
  public columnDefs: ColDef[] = [];
  public gridOptions: GridOptions;
  public dragging = false;
  public windowTop = 39;
  public windowLeft = 950;
  public groupIncludeFooter = true;
  public groupIncludeTotalFooter = true;
  public isChartOpen = false;
  public topPosition = 50;
  public leftPosition = 50;
  public startDate: string;
  public endDate: string;
  public sideBarDef: SideBarDef;
  public gridApi: GridApi<GetWhiteLabelRfqItem>;
  public columnApi: ColumnApi;
  public filterForm: FormGroup;
  private agGridService = inject(AgGridService);
  private renderer = inject(Renderer2);
  private backToBackService = inject(BackToBackService);
  private winIdx = inject(WindowIndexService);
  private elementRef = inject(ElementRef);
  private alerts = inject(AlertsService);
  private configService = inject(ConfigService);

  public ngOnInit(): void {
    this.sideBarDef = this.agGridService.sideBarDef;
    this.gridOptions = this.initializeGridOptions();

    this.filterForm = new FormGroup({
      start_date: new FormControl(''),
      end_date: new FormControl('')
    });
    this.loadHeadersFromSettings();
  }

  public loadHeadersFromSettings(): void {
    const headersNames = this.configService
      .userSettings()
      .ag_grid_config.find(grid => grid.identifier === 'wlPnlHeaders').headers;

    const headers = headersNames
      .map(fieldName =>
        this.agGridService.headers.find(header => header.field === fieldName)
      )
      .filter(header => header);

    this.columnDefs = headers as ColDef[];

    if (this.gridApi) {
      this.gridApi.setColumnDefs(this.columnDefs);
      this.gridApi.refreshHeader();
    }
  }

  public onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;
    if (this.columnState) {
      applyColumnState(params.columnApi, clone(this.columnState));
    }
    params.columnApi.autoSizeAllColumns();
  }

  public closeGrid(): void {
    this.backToBackService.removeBackToBackGrid();
  }

  public dragStart(): void {
    this.dragging = true;
  }

  public async dragEnd(): Promise<void> {
    this.dragging = false;
    const el = (this.elementRef.nativeElement as HTMLElement).querySelector(
      '.back-to-back-table-drag'
    );
    const rect = el.getBoundingClientRect();

    let { x, y } = this.dragEl.getFreeDragPosition();

    if (rect.x < WIN_POSITION_MIN_X) {
      x += WIN_POSITION_MIN_X - rect.x;
    }
    if (rect.y < WIN_POSITION_MIN_Y) {
      y += WIN_POSITION_MIN_Y - rect.y;
    }
    if (rect.x > window.innerWidth - WIN_POSITION_RIGHT_MIN) {
      x -= rect.x - (window.innerWidth - WIN_POSITION_RIGHT_MIN);
    }
    this.dragEl.setFreeDragPosition({ x, y });
  }

  public updateZIndex(): void {
    this.winIdx.setWindowIndex(
      this.elementRef.nativeElement.querySelector('.back-to-back-table-drag'),
      this.renderer
    );
  }

  public gridStateChanged(event: AgGridEvent): void {
    const allState = event.columnApi.getColumnState();
    this.backToBackService.saveBackToBackGridState(allState);
  }

  public clearFilters(): void {
    this.gridApi.setFilterModel(null);
    this.columnApi.resetColumnState();
    this.gridApi.onFilterChanged();
    this.filterForm.reset({
      start_date: '',
      end_date: ''
    });

    this.rowData = [];
    this.gridApi.setRowData(this.rowData);

    this.closeChart();

    this.columnApi.autoSizeAllColumns();
    const columnState = this.columnApi.getColumnState();
    this.backToBackService.saveBackToBackGridState(columnState);
  }

  public filterData(): void {
    this.startDate = this.filterForm.get('start_date')?.value;
    this.endDate = this.filterForm.get('end_date')?.value;

    if (!this.startDate || !this.endDate) {
      console.warn('No start date or end date provided');
      return;
    }

    const filterStartDate = new Date(this.startDate);
    const filterEndDate = new Date(this.endDate);

    if (isNaN(filterStartDate.getTime()) || isNaN(filterEndDate.getTime())) {
      console.error('Invalid start date or end date provided');
      return;
    }

    this.backToBackService
      .getWhiteLabelRfqBlotter(this.startDate, this.endDate)
      .subscribe({
        next: items => {
          this.rowData = items.filter(trade => {
            let tradeDate: Date | null = null;

            if (trade.timestamp_str) {
              tradeDate = new Date(trade.timestamp_str);
            }

            if (!tradeDate || isNaN(tradeDate.getTime())) {
              console.error('Invalid or missing trade date for:', trade);
              return false;
            }

            return tradeDate >= filterStartDate && tradeDate <= filterEndDate;
          });

          this.gridApi.setRowData(this.rowData);
        },
        error: e => {
          console.error('Error fetching data:', e);
          this.alerts.addAlert({
            message: 'Unable to fetch data',
            type: 'error'
          });
        }
      });
  }

  public openChart(): void {
    if (!this.isChartOpen) {
      this.isChartOpen = true;
    } else {
      this.updateZIndex();
    }
  }

  public closeChart(): void {
    this.isChartOpen = false;
  }

  private initializeGridOptions(): GridOptions {
    this.columnDefs = [
      {
        headerName: 'Timestamp',
        field: 'timestamp_str'
      }
    ];

    return {
      groupIncludeFooter: false,
      defaultColDef: {
        width: undefined,
        resizable: true,
        sortable: true,
        filter: true
      },
      columnDefs: this.columnDefs,
      rowData: this.rowData
    };
  }
}
